import type { Location } from "@/Domain/Location";
import type { WasherFacilities } from "@/Domain/WasherFacilities";

export type SuccessResponse<T> = {
  success: true;
  response: T;
};

export type ErrorResponse = {
  success: false;
  error: Error;
};

export type GetBasicRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
};

export type Response<T> = SuccessResponse<T> | ErrorResponse;

// API is a huge composite type of comma separated types. EG: export type API { key1:string, key2:number, ...} except that, instead of simple types like string and number, we define more complex types.
//  Each such type always has a first property named "request" followed by a property named "response"
export type API = {
  Loc: {
    request: {};
    response: Location[];
  };
};

const get = <T extends keyof API>(
  endpoint: T,
  request: API[T]["request"],
  searchValue: string
): Promise<Response<API[T]["response"]>> => {
  //console.log('get() request type = ' + endpoint +', request data = ' + JSON.stringify(request))

  const records: Record<string, string> = {};

  let k: keyof typeof request; // union of all keys in the request object, whatever type it may be.
  for (k in request) {
    if (!Object.prototype.hasOwnProperty.call(request, k)) continue;
    const value = request[k] as unknown;
    records[k.toString()] = encodeURIComponent(String(value));
  }

  return fetch(
    `https://nominatim.openstreetmap.org/search?q=${searchValue}&format=json`,
    {}
  )
    .then((response) =>
      response.json().then((x) =>
        response.ok
          ? ({
              success: true,
              response: x,
            } as SuccessResponse<any>)
          : ({
              success: false,
              error: new Error(x.toString()),
            } as ErrorResponse)
      )
    )
    .catch(
      (error) =>
        ({
          success: false,
          error,
        } as ErrorResponse)
    );
};

const getWasherFacilitiesAsync = async (): Promise<Response<WasherFacilities>> => {
  try {
    const response = await fetch(
      `https://oukosher.org/kosher-api/washers/get-facilities`,
      {}
    );
    const x = await response.json();
    return response.ok
      ? ({
          success: true,
          response: x,
        } as SuccessResponse<WasherFacilities>)
      : ({
          success: false,
          error: new Error(x.toString()),
        } as ErrorResponse);
  } catch (error) {
    return {
      success: false,
      error,
    } as ErrorResponse;
  }
};

export const api = { get, getWasherFacilitiesAsync };
